import {AppUrls} from 'shared/config/model/urls';

import React, {ReactNode} from 'react';
import {Control, UseFormRegister} from 'react-hook-form';
import {Link} from 'react-router-dom';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import HintQuestion from 'shared/ui/Hint/HintQuestion';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import {INotifyChannel, INotifyChannelSettings, isIdSelected, NotifyDelaySelector} from 'entities/notification';
import {HintPremium} from 'entities/tariff';
import useNotifyChannel from 'features/http/model/useNotifyChannel';

import styles from './NotifyChannelBlock.module.scss';

interface INotifyChannelBlockProps<T extends INotifyChannel> {
    items: T[] | undefined,
    isLoading: boolean,
    getLabel: (_item: T) => string,
    control: Control<any>,
    register: UseFormRegister<any>,
    fieldName: string,
    settingsField: string,
    settings: INotifyChannelSettings[],
    icon: ReactNode,
    connectIcon: ReactNode,
    title: ReactNode,
    notConnectedText: ReactNode,
    hint: string,
    premiumCondition?: React.ComponentProps<typeof HintPremium>['condition'],
    bordered?: boolean,
    last?: boolean,
}

const NotifyChannelBlock = <T extends INotifyChannel>({
    items,
    isLoading,
    getLabel,
    control,
    register,
    fieldName,
    settingsField,
    settings,
    icon,
    connectIcon,
    title,
    notConnectedText,
    hint,
    premiumCondition,
    bordered,
    last,
}: INotifyChannelBlockProps<T>) => {
    const {mode, selected, checkedCount} = useNotifyChannel(control, fieldName, isLoading, items, !!premiumCondition);

    if (mode === null) return null;

    return (
        <div className={cn(styles.container, bordered && styles.bordered, last && styles.last)}>
            {
                mode === 'free' && premiumCondition && (
                    <div className={cn(styles.content, styles.freeTariff)}>
                        <HintPremium condition={premiumCondition}/>
                        <span className={styles.text}>{notConnectedText}</span>
                        <span className={styles.tooltip}>
                            <HintQuestion>{hint}</HintQuestion>
                        </span>
                    </div>
                )
            }
            {
                mode === 'connect' && (
                    <div className={styles.blockInfo}>
                        <div className={styles.content}>
                            {connectIcon}
                            <span className={styles.text}>{notConnectedText}</span>
                        </div>
                        <div className={styles.contentAction}>
                            <Link
                                className={cn(
                                    styles.link,
                                    styles.colorLink,
                                )}
                                to={AppUrls.SETTINGS}
                            >
                                {t`Подключить`}
                            </Link>
                            <HintQuestion placement="top-start">{hint}</HintQuestion>
                        </div>
                    </div>
                )
            }
            {
                mode === 'list' && items && (
                    <div>
                        <div className={styles.subHeader}>
                            {icon}
                            <span className={styles.title}>
                                {title} {checkedCount > 0 && `(${checkedCount})`}
                            </span>
                        </div>
                        <div className={styles.list}>
                            {
                                items.map((item, index) => (
                                    <div key={item.id} className={styles.contentNotFree}>
                                        <Checkbox
                                            textStyle={styles.checkbox}
                                            value={`${item.id}`}
                                            {...register(fieldName)}
                                        >
                                            {getLabel(item)}
                                        </Checkbox>
                                        {isIdSelected(selected, item.id) && (
                                            <NotifyDelaySelector
                                                settingsField={settingsField}
                                                control={control}
                                                register={register}
                                                values={settings}
                                                itemId={item.id}
                                                index={index}
                                            />
                                        )}
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default NotifyChannelBlock;
